import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Ban, Trash2, RotateCcw, ShieldAlert } from 'lucide-react';

const VIEW_TABS = [
  { v: 'active', label: 'Active' },
  { v: 'spam', label: 'Spam' },
  { v: 'deleted', label: 'Trash' },
];

export function ViewTabs({ view, setView, counts = {}, testidPrefix = 'mod' }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid={`${testidPrefix}-view-tabs`}>
      {VIEW_TABS.map((t) => {
        const c = t.v === 'spam' ? counts.spam : t.v === 'deleted' ? counts.deleted : null;
        return (
          <button
            key={t.v}
            onClick={() => setView(t.v)}
            data-testid={`${testidPrefix}-view-${t.v}`}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === t.v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            {t.label}
            {c ? <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${view === t.v ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{c}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

// Small kebab menu. Render as a SIBLING of the clickable row (never nested in a <button>).
// The dropdown is portaled to <body> with fixed positioning so no ancestor's overflow:hidden can clip it.
export function ModerationMenu({ view = 'active', isAdmin = false, onSpam, onDelete, onRestore, onPurge, testid = 'record', align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // null until measured → avoids (0,0) flash
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const close = () => setOpen(false);

  const place = useCallback(() => {
    if (!btnRef.current) return;
    const MENU_W = 192; // w-48
    const rect = btnRef.current.getBoundingClientRect();
    const menuH = menuRef.current ? menuRef.current.offsetHeight : 90;
    let left = align === 'right' ? rect.right - MENU_W : rect.left;
    left = Math.min(Math.max(8, left), window.innerWidth - MENU_W - 8);
    const openUp = rect.bottom + 4 + menuH > window.innerHeight;
    const top = openUp ? Math.max(8, rect.top - menuH - 4) : rect.bottom + 4;
    setPos({ top, left });
  }, [align]);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => { window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [open, place]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o); }}
        data-testid={`${testid}-menu-btn`}
        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        aria-label="More actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); e.preventDefault(); close(); }} />
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? 'visible' : 'hidden' }}
            className="z-[9999] w-48 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            data-testid={`${testid}-menu`}
          >
            {view === 'active' ? (
              <>
                <Item icon={Ban} label="Mark as spam" onClick={() => { close(); onSpam && onSpam(); }} testid={`${testid}-spam`} />
                <Item icon={Trash2} label="Delete" danger onClick={() => { close(); onDelete && onDelete(); }} testid={`${testid}-delete`} />
              </>
            ) : (
              <>
                <Item icon={RotateCcw} label="Restore" onClick={() => { close(); onRestore && onRestore(); }} testid={`${testid}-restore`} />
                {isAdmin && <Item icon={ShieldAlert} label="Delete permanently" danger onClick={() => { close(); onPurge && onPurge(); }} testid={`${testid}-purge`} />}
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function Item({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${danger ? 'text-rose-600' : 'text-slate-700'}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

// Row / header selection checkbox. Stops propagation so it never triggers row navigation.
export function RowCheck({ checked, onChange, testid, ariaLabel = 'Select' }) {
  return (
    <input
      type="checkbox"
      checked={!!checked}
      aria-label={ariaLabel}
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
      className="h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-[#0035A8] focus:ring-[#0035A8]"
    />
  );
}

// Sticky bulk action bar shown when 1+ rows are selected. Confirms before acting.
export function BulkBar({ count, label = 'items', onSpam, onTrash, onRestore, onClear, busy = false, testidPrefix = 'bulk', assignChildren = null }) {
  if (!count) return null;
  const doSpam = () => { if (window.confirm(`Mark ${count} ${label} as spam?\n\nThey'll move to the Spam tab and will NOT return on the next sync.`)) onSpam(); };
  const doTrash = () => { if (window.confirm(`Move ${count} ${label} to Trash?\n\nThey'll move to the Trash tab and will NOT return on the next sync. Nothing is permanently deleted.`)) onTrash(); };
  const doRestore = () => { if (window.confirm(`Restore ${count} ${label} back to Active?`)) onRestore(); };
  return (
    <div className="sticky top-2 z-30 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#0035A8]/25 bg-white px-4 py-2.5 shadow-md" data-testid={`${testidPrefix}-bar`}>
      <span className="text-sm font-semibold text-slate-800" data-testid={`${testidPrefix}-count`}>{count} selected</span>
      <div className="flex flex-wrap items-center gap-2">
        {assignChildren}
        {onRestore && (
          <button type="button" onClick={doRestore} disabled={busy} data-testid={`${testidPrefix}-restore`}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
            <RotateCcw className="h-3.5 w-3.5" /> Restore
          </button>
        )}
        {onSpam && (
        <button type="button" onClick={doSpam} disabled={busy} data-testid={`${testidPrefix}-spam`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60">
          <Ban className="h-3.5 w-3.5" /> Mark as spam
        </button>
        )}
        {onTrash && (
        <button type="button" onClick={doTrash} disabled={busy} data-testid={`${testidPrefix}-trash`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">
          <Trash2 className="h-3.5 w-3.5" /> Move to trash
        </button>
        )}
        <button type="button" onClick={onClear} data-testid={`${testidPrefix}-clear`}
          className="inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium text-slate-500 hover:bg-slate-100">
          Clear
        </button>
      </div>
    </div>
  );
}
